import {
  ArrowDropDown,
  Close,
  Done,
  FilterAltOutlined,
  Search,
} from "@mui/icons-material";
import {
  alpha,
  Badge,
  Box,
  Button,
  Paper,
  Popper,
  ClickAwayListener,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useState, useRef } from "react";

export default function FilterCities({
  data = {},
  elementsFilter = [],
  setElementsFilter = () => {},
  citiesSwitchMakerVisible = [],
}) {
  const theme = useTheme();
  const [filterCities, setFilterCities] = useState("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const isDownMd = useMediaQuery(theme.breakpoints.down("lg"));
  const numberExibitionTags = isDownMd ? 1 : 3;

  const styles = {
    container: {
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      left: { xs: "60px", md: "70px" },
      transition: "left 0.3s ease",
      top: "15px",
      alignItems: "center",
      gap: 1,
      justifyContent: "center",
      zIndex: 10,
      flexDirection: "row",
      fontWeight: "700",
    },

    buttonFilter: {
      textTransform: "capitalize",
      color: theme.palette.text.primary,
      border: "1px solid " + theme.palette.divider,
    },

    badge: {
      "& .MuiBadge-badge": { top: 4 },
    },

    tagPaper: {
      border: "1px solid " + theme.palette.divider,
    },

    tagButton: {
      textTransform: "capitalize",
      color: theme.palette.text.primary,
    },

    popper: {
      zIndex: 1600,
      width: 250,
      height: "400px",
    },

    popperContent: {
      border: "1px solid " + theme.palette.divider,
      boxShadow: "0px 0px 3px #0000001a",
      backgroundColor: theme.palette.background.paper,
      borderRadius: 1,
      marginTop: "8px",
      maxHeight: 400,
      overflow: "auto",
    },

    menuHeader: {
      width: "100%",
      fontSize: "14px",
      justifyContent: "space-between",
      alignItems: "center",
      pl: 2,
      pr: 1,
      mt: 2,
    },

    menuClearButton: {
      textTransform: "none",
      fontSize: "0.85rem",
    },

    menuItem: (selected) => ({
      background: selected ? alpha(theme.palette.primary.main, 0.1) : "none",
      borderRadius: 1,
      margin: "2px 8px",
      "&:hover": {
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.2)
          : alpha(theme.palette.action.hover, 0.04),
      },
    }),

    textField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        backgroundColor: theme.palette.background.paperLinkedMaps,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: theme.palette.background.paperLinkedMaps,
        },
      },
    },
  };

  const cidades = Object.entries(data)
    .map(([nome, info]) => ({
      nome,
      codigo: info.codigo,
    }))
    .filter(({ codigo }) => citiesSwitchMakerVisible.includes(codigo))
    .filter(({ nome }) =>
      nome.toLowerCase().includes(filterCities.toLowerCase())
    );

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function addFilter(nome, codigo) {
    setElementsFilter((prev) => [...prev, { nome, codigo }]);
  }

  function removeFilter(codigo) {
    setElementsFilter((prev) => prev.filter((f) => f.codigo !== codigo));
  }

  const MenuItemFilter = ({ nome, codigo }) => {
    const selected = elementsFilter.some((f) => f.codigo === codigo);

    return (
      <Box
        onClick={() =>
          selected ? removeFilter(codigo) : addFilter(nome, codigo)
        }
        sx={styles.menuItem(selected)}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
          px={2}
          py={1}
          sx={{ cursor: "pointer" }}
        >
          <Typography
            sx={{
              color: selected ? theme.palette.primary.main : "inherit",
              textTransform: "capitalize",
              fontSize: "0.875rem",
            }}
          >
            {nome}
          </Typography>
          {selected && <Done color="primary" fontSize="small" />}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Stack sx={styles.container}>
        <Paper elevation={0} ref={anchorRef}>
          <Button
            startIcon={<FilterAltOutlined />}
            endIcon={<ArrowDropDown />}
            onClick={handleToggle}
            sx={styles.buttonFilter}
          >
            {"Filtros"}
          </Button>
        </Paper>

        {/* TAGS ATIVAS */}

        {elementsFilter
          .slice(0, numberExibitionTags)
          .map(({ nome, codigo }) => (
            <Tooltip key={codigo} title={nome} arrow>
              <Paper key={codigo} elevation={0} sx={styles.tagPaper}>
                <Button
                  endIcon={<Close />}
                  onClick={() => removeFilter(codigo)}
                  sx={styles.tagButton}
                >
                  <Typography
                    sx={{
                      maxWidth: { xs: "50px", md: "100px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {nome}
                  </Typography>
                </Button>
              </Paper>
            </Tooltip>
          ))}
        {elementsFilter.length > numberExibitionTags && (
          <Tooltip
            key="more"
            title={
              <div>
                {elementsFilter.slice(numberExibitionTags).map((e, index) => (
                  <div key={e.codigo}>
                    {e.nome}
                    {index <
                      elementsFilter.length - numberExibitionTags - 1 && <br />}
                  </div>
                ))}
              </div>
            }
            arrow
          >
            <Paper key="more" elevation={0} sx={styles.tagPaper}>
              <Button sx={styles.tagButton}>
                +{elementsFilter.length - numberExibitionTags}
              </Button>
            </Paper>
          </Tooltip>
        )}
      </Stack>

      {/* POPPER DE FILTROS */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={styles.popper}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={styles.popperContent}>
            <Stack direction="row" sx={styles.menuHeader}>
              <Typography>Adicionar Filtro</Typography>
              <Button
                variant="text"
                size="small"
                sx={styles.menuClearButton}
                onClick={() => setElementsFilter([])}
              >
                Limpar
              </Button>
            </Stack>

            <Box p={1.5}>
              <TextField
                placeholder="Buscar"
                autoComplete="off"
                variant="outlined"
                size="small"
                value={filterCities}
                onChange={(event) => setFilterCities(event.target.value)}
                sx={styles.textField}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Search sx={{ color: theme.palette.action.disabled }} />
                    ),
                  },
                }}
                autoFocus
              />
            </Box>

            <Box sx={{ maxHeight: 250, overflow: "auto", pb: 1 }}>
              {cidades
                .sort((a, b) => a.nome.localeCompare(b.nome))
                .map(({ nome, codigo }) => (
                  <MenuItemFilter key={codigo} nome={nome} codigo={codigo} />
                ))}
              {cidades.length === 0 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    py: 2,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Nenhum resultado encontrado
                </Typography>
              )}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
